<template>
  <div class="sale-by-customers-report">
    <v-card flat outlined class="report-table-card">
      <v-card-title class="report-table-header">
        <div>
          <div class="report-title">Customer Sales</div>
          <div class="report-subtitle">
            {{ User.length }} customers found
          </div>
        </div>

        <v-spacer />

        <div class="summary-box">
          <div class="summary-label">Total Sale</div>
          <div class="summary-value">{{ totalReportSale | currency }}</div>
        </div>
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-0">
        <v-progress-linear v-if="isLoading" indeterminate height="3" />

        <v-data-table
          v-else
          dense
          :sort-by="sortBy"
          :mobile-breakpoint="0"
          :headers="headers"
          :items="User"
          :items-per-page="-1"
          hide-default-footer
          class="customer-sale-table"
        >
          <template v-slot:item="{ item }">
            <tr>
              <td class="text-left">
                <v-btn
                  class="no-print"
                  icon
                  small
                  @click="toggleExpand(item)"
                >
                  <v-icon v-if="expandedRow === item">mdi-chevron-up</v-icon>
                  <v-icon v-else>mdi-chevron-down</v-icon>
                </v-btn>
              </td>

              <td class="text-left">
                <span class="customer-name">
                  {{ getCustomerName(item) }}
                </span>
              </td>

              <td class="text-right">
                <span class="money">
                  {{ item.totalSale | currency }}
                </span>
              </td>
            </tr>

            <tr v-if="expandedRow === item">
              <td :colspan="headers.length" class="pa-0">
                <div class="expanded-panel">
                  <Invoice
                    :CustomerId="item.CustomerId"
                    :start="start"
                    :end="end"
                  />
                </div>
              </td>
            </tr>
          </template>

          <template v-slot:body.append v-if="User.length > 0">
            <tr class="total-row">
              <td></td>
              <td class="font-weight-bold">Total</td>
              <td class="text-right font-weight-bold">
                {{ totalReportSale | currency }}
              </td>
            </tr>
          </template>

          <template v-slot:no-data>
            <div class="empty-state">
              No customer sales found for this date range.
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import invoiceService from "@/modules/Invoice/service";

const Invoice = () => import("@/modules/ReportView/GlobalReport/Invoice.vue");

export default {
  name: "sale-by-customers-report",

  components: {
    Invoice,
  },

  props: ["start", "reset", "end", "CustomerId"],

  data() {
    return {
      isLoading: true,
      sortBy: "name",
      User: [],
      expandedRow: null,
      headers: [
        {
          text: "",
          align: "left",
          value: "expand",
          sortable: false,
          width: 60,
        },
        {
          text: "Customer",
          align: "left",
          value: "name",
          sortable: true,
        },
        {
          text: "Sale",
          align: "right",
          value: "totalSale",
          sortable: true,
        },
      ],
    };
  },

  computed: {
    totalReportSale() {
      return this.User.reduce(
        (sum, row) => sum + Number(row.totalSale || 0),
        0
      );
    },

    exportRows() {
      return this.User.map((row) => ({
        customerName: this.getCustomerName(row),
        totalSale: Number(row.totalSale || 0),
      }));
    },
  },

  async created() {
    await this.getCustomerSale();
  },

  methods: {
    getCustomerName(row) {
      if (row.Customer && row.Customer.name) return row.Customer.name;
      return "N/A";
    },

    toggleExpand(item) {
      this.expandedRow = this.expandedRow === item ? null : item;
    },

    async getCustomerSale() {
      const where = {};

      if (this.start) where.start = this.start;
      if (this.end) where.end = this.end;

      try {
        const result = await invoiceService.invoiceByCustomer(where);

        this.User = result.data || [];
        this.$emit("export-data", this.exportRows);
      } finally {
        this.$Progress.finish();
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.sale-by-customers-report {
  width: 100%;
}

.report-table-card {
  border-radius: 16px;
  overflow: hidden;
}

.report-table-header {
  background: #fff;
  padding: 18px 20px;
}

.report-title {
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
}

.report-subtitle {
  font-size: 13px;
  color: #6b7280;
  margin-top: 3px;
}

.summary-box {
  min-width: 120px;
  padding: 8px 12px;
  margin-left: 8px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  text-align: right;
  background: #f9fafb;
}

.summary-label {
  font-size: 11px;
  color: #6b7280;
  text-transform: uppercase;
}

.summary-value {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
}

.customer-name {
  font-weight: 600;
  text-transform: capitalize;
  color: #111827;
}

.money {
  font-weight: 700;
}

.expanded-panel {
  padding: 14px;
  background: #f3f8fc;
  border-top: 1px solid #e5e7eb;
}

.total-row {
  background: #f9fafb;
}

.empty-state {
  padding: 32px;
  text-align: center;
  color: #6b7280;
}

.customer-sale-table ::v-deep th {
  font-size: 12px;
  font-weight: 700;
  color: #374151 !important;
  background: #f9fafb;
  text-transform: uppercase;
}

.customer-sale-table ::v-deep td {
  font-size: 13px;
}

@media print {
  .report-table-card {
    border: none !important;
    border-radius: 0;
  }

  .report-table-header {
    padding: 10px 0;
  }

  .summary-box {
    border: none;
    background: #fff;
  }

  .no-print {
    display: none !important;
  }
}
</style>